import { makeAutoObservable, runInAction } from 'mobx';
import Cookies from 'js-cookie';

class ThemeStore {
	theme = 'light';

	constructor() {
		makeAutoObservable(this);
		this.initializeTheme();
	}

	initializeTheme = () => {
		// Check cookie first
		const savedTheme = Cookies.get('theme');

		if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
			runInAction(() => {
				this.theme = savedTheme;
			});
		} else {
			// Check system preference
			const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
			const systemTheme = prefersDark ? 'dark' : 'light';

			runInAction(() => {
				this.theme = systemTheme;
			});

			// Save to cookie
			Cookies.set('theme', systemTheme, { sameSite: 'Strict' });
		}

		// Apply theme class to body
		this.applyThemeToBody();

		// Listen for system theme changes
		window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', this.handleSystemThemeChange);
	};

	applyThemeToBody = () => {
		// Remove both classes first
		document.body.classList.remove('light', 'dark');
		// Add current theme class
		document.body.classList.add(this.theme);
	};

	handleSystemThemeChange = (e) => {
		// Only update if user hasn't manually set a preference
		const savedTheme = Cookies.get('theme');
		if (!savedTheme) {
			const newTheme = e.matches ? 'dark' : 'light';
			runInAction(() => {
				this.theme = newTheme;
			});
			this.applyThemeToBody();
		}
	};

	toggleTheme = () => {
		const newTheme = this.theme === 'light' ? 'dark' : 'light';

		runInAction(() => {
			this.theme = newTheme;
		});

		// Save to cookie
		Cookies.set('theme', newTheme, { sameSite: 'Strict' });

		// Apply to body
		this.applyThemeToBody();
	};

	setTheme = (newTheme) => {
		if (newTheme !== 'light' && newTheme !== 'dark') {
			console.warn(`Invalid theme: ${newTheme}. Must be 'light' or 'dark'.`);
			return;
		}

		runInAction(() => {
			this.theme = newTheme;
		});

		// Save to cookie
		Cookies.set('theme', newTheme, { sameSite: 'Strict' });

		// Apply to body
		this.applyThemeToBody();
	};

	get isDark() {
		return this.theme === 'dark';
	}

	get isLight() {
		return this.theme === 'light';
	}

	cleanup = () => {
		// Remove event listener on cleanup
		window.matchMedia('(prefers-color-scheme: dark)').removeEventListener('change', this.handleSystemThemeChange);
	};
}

export const themeStore = new ThemeStore();
